(() => {
  'use strict';

  if (window.__smCartAjaxFixInstalled) return;
  window.__smCartAjaxFixInstalled = true;

  const addStyles = () => {
    if (document.getElementById('sm-cart-ajax-fix-styles')) return;

    const style = document.createElement('style');
    style.id = 'sm-cart-ajax-fix-styles';
    style.textContent = `
      [data-cart-item].sm-cart-line-updating {
        opacity: .62;
        pointer-events: none;
        transition: opacity 140ms ease;
      }

      [data-cart-item].sm-cart-line-updating .m-quantity {
        position: relative;
      }

      [data-cart-item].sm-cart-line-updating .m-quantity::after {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        width: 17px;
        height: 17px;
        margin: -8.5px 0 0 -8.5px;
        border: 2px solid rgba(17, 17, 17, .18);
        border-top-color: #111;
        border-radius: 50%;
        animation: smCartAjaxSpin .65s linear infinite;
      }

      @keyframes smCartAjaxSpin {
        to { transform: rotate(360deg); }
      }

      @media (prefers-reduced-motion: reduce) {
        [data-cart-item].sm-cart-line-updating {
          transition: none;
        }

        [data-cart-item].sm-cart-line-updating .m-quantity::after {
          animation-duration: 1.4s;
        }
      }
    `;
    document.head.appendChild(style);
  };

  const getRoot = () => {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root || '/';
  };

  const collectSections = (fallbackSection) => {
    const sections = [];

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections }
      })
    );

    if (fallbackSection) sections.push(fallbackSection);
    sections.push('cart-count');

    return Array.from(new Set(sections.filter(Boolean)));
  };

  const normalizeError = (payload, fallback) => {
    if (!payload) return fallback;
    if (typeof payload === 'string') return payload;
    if (typeof payload.errors === 'string') return payload.errors;
    if (payload.description) return payload.description;
    if (payload.message) return payload.message;

    if (payload.errors && typeof payload.errors === 'object') {
      const firstError = Object.values(payload.errors).flat()[0];
      if (firstError) return String(firstError);
    }

    return fallback;
  };

  const showWarning = (target, message) => {
    if (
      window.MinimogTheme &&
      window.MinimogTheme.Notification &&
      typeof window.MinimogTheme.Notification.show === 'function'
    ) {
      window.MinimogTheme.Notification.show({
        target: target || document.body,
        method: 'appendChild',
        type: 'warning',
        message,
        last: 3500,
        sticky: false
      });
      return;
    }

    console.warn(message);
  };

  const readCountFromSections = (payload) => {
    if (Number.isFinite(Number(payload && payload.item_count))) {
      return Number(payload.item_count);
    }

    const sectionHtml = payload && payload.sections && payload.sections['cart-count'];
    if (!sectionHtml) return null;

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const countNode = parsed.querySelector('.m-cart-count, m-cart-count');
    if (!countNode) return null;

    const count = parseInt(countNode.textContent.trim(), 10);
    return Number.isFinite(count) ? count : null;
  };

  const syncStrideMartCartCount = (payload) => {
    const itemCount = readCountFromSections(payload);
    if (itemCount === null) return;

    document.querySelectorAll('.smv2-cart-count').forEach((count) => {
      count.textContent = itemCount > 99 ? '99+' : String(itemCount);
      count.hidden = itemCount === 0;
    });
  };

  const emitCartUpdate = (payload) => {
    if (
      window.MinimogEvents &&
      typeof window.MinimogEvents.emit === 'function' &&
      window.MinimogTheme &&
      window.MinimogTheme.pubSubEvents &&
      window.MinimogTheme.pubSubEvents.cartUpdate
    ) {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { cart: payload });
      syncStrideMartCartCount(payload);
      return true;
    }

    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        detail: { open: true }
      })
    );
    return false;
  };

  const setLineBusy = (cartItems, line, target, busy) => {
    const lineItem =
      (target && target.closest && target.closest('[data-cart-item]')) ||
      cartItems.querySelector(`[data-cart-item][data-index="${line}"]`);

    if (!lineItem) return null;

    lineItem.classList.toggle('sm-cart-line-updating', busy);
    if (busy) {
      lineItem.setAttribute('aria-busy', 'true');
    } else {
      lineItem.removeAttribute('aria-busy');
    }

    return lineItem;
  };

  const patchCartItems = () => {
    const CartItems = customElements.get('m-cart-items');
    if (!CartItems || CartItems.prototype.__smFastCartPatched) return;

    Object.defineProperty(CartItems.prototype, '__smFastCartPatched', {
      value: true,
      configurable: false,
      enumerable: false,
      writable: false
    });

    CartItems.prototype.updateQuantity = async function (line, quantity, name, target) {
      const requestNumber = (this.__smCartRequestNumber || 0) + 1;
      this.__smCartRequestNumber = requestNumber;

      if (this.__smCartAbortController) {
        this.__smCartAbortController.abort();
      }

      const controller = new AbortController();
      this.__smCartAbortController = controller;

      const lineItem = setLineBusy(this, line, target, true);
      const fallbackSection = this.dataset.sectionName || (this.isCartPage ? 'cart-template' : 'cart-drawer');
      const sections = collectSections(fallbackSection);
      const routes = (window.MinimogSettings && window.MinimogSettings.routes) || {};
      const changeUrl = routes.cart_change_url || `${getRoot()}cart/change.js`;
      const nextQuantity = Math.max(0, parseInt(quantity, 10) || 0);

      try {
        const response = await fetch(changeUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            line: Number(line),
            quantity: nextQuantity,
            sections,
            sections_url: window.location.pathname
          }),
          signal: controller.signal
        });

        let cart;
        try {
          cart = await response.json();
        } catch (parseError) {
          throw new Error('The cart response could not be read. Please try again.');
        }

        if (!response.ok || cart.errors) {
          throw new Error(normalizeError(cart, 'Unable to update your cart. Please try again.'));
        }

        if (requestNumber !== this.__smCartRequestNumber) return;
        emitCartUpdate(cart);
      } catch (error) {
        if (error && error.name === 'AbortError') return;

        const message = error && error.message
          ? error.message
          : 'Unable to update your cart. Please try again.';

        if (target && document.contains(target)) {
          target.value = target.defaultValue;
        }

        if (typeof this.updateLiveRegions === 'function') {
          this.updateLiveRegions(line, message);
        } else {
          showWarning(lineItem || this, message);
        }
      } finally {
        if (requestNumber === this.__smCartRequestNumber) {
          this.__smCartAbortController = null;
          setLineBusy(this, line, target, false);
        }
      }
    };
  };

  const addUpsellItem = async (button) => {
    if (!button || button.dataset.smAdding === 'true') return;

    const variantId = parseInt(button.getAttribute('data-variant-id'), 10);
    if (!variantId) return;

    button.dataset.smAdding = 'true';
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding…';

    const sections = collectSections('cart-drawer');
    const addUrl = `${getRoot()}cart/add.js`;

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections,
          sections_url: window.location.pathname
        })
      });

      let cart;
      try {
        cart = await response.json();
      } catch (parseError) {
        throw new Error('The cart response could not be read. Please try again.');
      }

      if (!response.ok || cart.status || cart.errors) {
        throw new Error(normalizeError(cart, 'Unable to add this item. Please try again.'));
      }

      button.textContent = 'Added';
      emitCartUpdate(cart);

      document.dispatchEvent(
        new CustomEvent('product-ajax:added', {
          detail: { product: cart }
        })
      );
    } catch (error) {
      button.dataset.smAdding = 'false';
      button.disabled = false;
      button.textContent = originalText;
      showWarning(button.closest('[data-sm-upsell-card]') || button, error.message || 'Unable to add this item.');
    }
  };

  addStyles();

  customElements.whenDefined('m-cart-items').then(patchCartItems);

  document.addEventListener(
    'click',
    (event) => {
      const origin = event.target instanceof Element ? event.target : null;
      if (!origin) return;

      const quantityButton = origin.closest('m-quantity-input button');
      if (quantityButton) {
        event.preventDefault();
      }

      const upsellButton = origin.closest('[data-sm-upsell-add]');
      if (!upsellButton) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      addUpsellItem(upsellButton);
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter') return;
      const origin = event.target instanceof Element ? event.target : null;
      if (!origin || !origin.closest('m-quantity-input')) return;

      event.preventDefault();
      origin.blur();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const source = event.submitter || document.activeElement;
      if (!(source instanceof Element)) return;

      if (source.closest('m-quantity-input') || source.closest('[data-sm-upsell-add]')) {
        event.preventDefault();
      }
    },
    true
  );
})();
